import { useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { PricingService } from "@/services/PricingService";
import type { ChecklistComponent } from "@/types/components";

interface PricingTestCase {
  component_id: number;
  test_value: any;
  test_quantity?: number;
  expected_result?: number;
}

interface UsePricingConfiguratorLogicProps {
  getAllComponents: () => ChecklistComponent[];
  testCases: PricingTestCase[];
  setPreviewResults: (results: any[]) => void;
  setShowPreviewDialog: (show: boolean) => void;
  savePricingMutation: any;
}

export function usePricingConfiguratorLogic({
  getAllComponents,
  testCases,
  setPreviewResults,
  setShowPreviewDialog,
  savePricingMutation,
}: UsePricingConfiguratorLogicProps) {
  const { toast } = useToast();

  const testPricing = useCallback(() => {
    const results: any[] = [];

    getAllComponents().forEach((component) => {
      const testCase = testCases.find((tc) => tc.component_id === (component.id || 0));
      if (!testCase) return;

      try {
        const breakdown = PricingService.calculateComponentPrice(
          component,
          testCase.test_value,
          testCase.test_quantity
        );

        results.push({
          component,
          testCase,
          breakdown,
          success: true,
        });
      } catch (error) {
        results.push({
          component,
          testCase,
          error: error instanceof Error ? error.message : "Unknown error",
          success: false,
        });
      }
    });

    setPreviewResults(results);
    setShowPreviewDialog(true);
  }, [getAllComponents, testCases, setPreviewResults, setShowPreviewDialog]);

  const validateFormula = useCallback(
    (formula: string, component: ChecklistComponent): string | null => {
      try {
        const testResult = PricingService.evaluateFormula(formula, 10, component.base_price || 0);
        if (isNaN(testResult)) {
          return "Formula produces invalid result";
        }
        return null;
      } catch (error) {
        return error instanceof Error ? error.message : "Invalid formula";
      }
    },
    []
  );

  const savePricingConfiguration = useCallback(
    (template: any) => {
      if (!template) return;

      // Validate all formulas first
      const components = getAllComponents();
      const formulaErrors: string[] = [];

      components.forEach((component) => {
        if (component.pricing_type === "formula" && component.formula) {
          const error = validateFormula(component.formula, component);
          if (error) {
            formulaErrors.push(`${component.label}: ${error}`);
          }
        }
      });

      if (formulaErrors.length > 0) {
        toast({
          title: "Formula Errors",
          description: formulaErrors.join("\n"),
          variant: "destructive",
        });
        return;
      }

      const pricingData = {
        last_updated: new Date().toISOString(),
        components: getAllComponents().map((component) => ({
          component_id: component.id,
          field_name: component.field_name,
          pricing_type: component.pricing_type,
          base_price: component.base_price,
          unit_label: component.unit_label,
          formula: component.formula,
          allow_quantity_input: component.allow_quantity_input,
          min_quantity: component.min_quantity,
          max_quantity: component.max_quantity,
          default_quantity: component.default_quantity,
          options:
            component.options?.map((option) => ({
              option_value: option.option_value,
              option_label: option.option_label,
              price_modifier: option.price_modifier,
              price_modifier_type: option.price_modifier_type,
            })) || [],
        })),
      };

      savePricingMutation.mutate(pricingData);
    },
    [getAllComponents, validateFormula, toast, savePricingMutation]
  );

  return {
    testPricing,
    validateFormula,
    savePricingConfiguration,
  };
}
